import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextareaAutosize,
  Typography,
} from "@mui/material"
import { CancelFilledOutlined, OpenInNewOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useMemo, useState } from "react"

import { useSendSalesQuoteEmail } from "../../hooks/useSendSalesQuoteEmail"
import type { NewRequestFormValues } from "../../types"
import { formatFullName } from "../../utils/format"

import { NoSalesSubscribersModal } from "./NoSalesSubscribersModal"
import { QuoteEmailPreviewDialog } from "./QuoteEmailPreviewDialog"

import { Button } from "@/components/common/Button"
import { FIELD_LABEL_CLASSES } from "@/constants/forms"
import { ContactChannel } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useGetContacts } from "@/screens/Companies/hooks/useGetContacts"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const QUOTE_SUBSCRIPTION_ID = "QUOTE"

interface Recipient {
  id: string
  name: string
  email: string
}

interface SendStepProps {
  formValues: NewRequestFormValues
  quoteGroupId: number
  onBack: () => void
  onSubmitted: () => void
}

export const SendStep: FC<SendStepProps> = ({ formValues, quoteGroupId, onBack, onSubmitted }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { sendSalesQuoteEmail, isSending } = useSendSalesQuoteEmail()

  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set())
  const [emailNote, setEmailNote] = useState("")
  const [isNoSubscribersWarningOpen, setIsNoSubscribersWarningOpen] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const { contactList, contactListFetching } = useGetContacts(formValues.customer.company.companyId)
  const customerRecipients = useMemo<Recipient[]>(() => {
    return contactList
      .filter(
        (contact) =>
          contact.isActive &&
          contact.contactId &&
          contact.contactSubscriptions?.some((sub) => sub?.contactSubscriptionId === QUOTE_SUBSCRIPTION_ID)
      )
      .map<Recipient | null>((contact) => {
        const email = contact.contactMethod?.find((method) => method?.contactChannel === ContactChannel.Email)?.value
        if (!contact.contactId || !email) return null
        return { id: contact.contactId, name: formatFullName(contact.firstName, contact.lastName), email }
      })
      .filter((recipient): recipient is Recipient => recipient !== null)
  }, [contactList])

  const isLoadingRecipients = contactListFetching && contactList.length === 0
  const contacts = customerRecipients.filter((recipient) => !removedIds.has(recipient.id))

  const handleContactRemove = (id: string) => {
    setRemovedIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  const handlePreviewEmail = () => {
    setIsPreviewOpen(true)
  }

  const handlePreviewBack = () => {
    setIsPreviewOpen(false)
  }

  const handleSendToSelf = () => {
    notify({ type: NotificationType.Warning, message: "Send to myself coming soon" })
  }

  const handleDownload = () => {
    notify({ type: NotificationType.Warning, message: "Download Quote coming soon" })
  }

  const handleSubmit = async () => {
    const isSuccess = await sendSalesQuoteEmail({
      quoteGroupId,
      emailReceiverContactIds: contacts.map((contact) => contact.id),
      emailNote,
    })
    if (!isSuccess) return
    if (contacts.length === 0) {
      setIsNoSubscribersWarningOpen(true)
      return
    }
    onSubmitted()
  }

  const handleNoSubscribersWarningClose = () => {
    setIsNoSubscribersWarningOpen(false)
    onSubmitted()
  }

  return (
    <>
      <DialogContent classes={{ root: "p-10 flex flex-col gap-6" }}>
        <Typography className='text-center text-gray-700' variant='body1'>
          This request will be sent to the following customer contacts who are subscribed to Quotes:
        </Typography>

        {isLoadingRecipients && (
          <Box className='flex justify-center py-2'>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoadingRecipients && contacts.length === 0 && (
          <Typography className='text-center text-sm text-gray-500'>
            No customer contacts are subscribed to Quotes for this account.
          </Typography>
        )}

        {!isLoadingRecipients && contacts.length > 0 && (
          <TableContainer component={Paper} elevation={0} className='border border-b-0 border-gray-400'>
            <Table data-testid='sales.send-to-customer-step.contacts-table'>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id} className='border-b border-gray-400'>
                    <TableCell className='p-2'>
                      <Typography className='text-sm text-gray-900'>{contact.name}</Typography>
                    </TableCell>
                    <TableCell className='p-2'>
                      <Typography className='text-sm text-gray-700'>{contact.email}</Typography>
                    </TableCell>
                    <TableCell className='p-2'>
                      {contacts.length > 1 && (
                        <IconButton
                          size='small'
                          onClick={() => handleContactRemove(contact.id)}
                          aria-label='Remove contact'
                          data-testid={`sales.send-to-customer-step.remove-contact-${contact.id}`}
                        >
                          <CancelFilledOutlined />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box>
          <label htmlFor='sales-send-to-customer-email-note' className={FIELD_LABEL_CLASSES}>
            Email Note
          </label>
          <TextareaAutosize
            id='sales-send-to-customer-email-note'
            value={emailNote}
            onChange={(event) => setEmailNote(event.target.value)}
            placeholder='Add your comments to be sent via email'
            minRows={3}
            maxRows={10}
            className='w-full overflow-y-auto rounded-md border border-solid border-gray-400 p-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#BFDBFE]'
            data-testid='sales.send-to-customer-step.email-note-field'
          />
        </Box>
      </DialogContent>

      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={onBack}
          disabled={isSending}
          data-testid='sales.send-to-customer-step.back-button'
        >
          Go Back
        </Button>
        <Box className='flex gap-2'>
          <Button
            appearance='outlined'
            endIcon={<OpenInNewOutlined />}
            onClick={handlePreviewEmail}
            disabled={isSending}
            data-testid='sales.send-to-customer-step.preview-email-button'
          >
            Preview Email
          </Button>
          <Button
            appearance='contained'
            onClick={handleSubmit}
            disabled={isSending}
            endIcon={
              isSending ? (
                <Box className='flex items-center'>
                  <CircularProgress size={16} />
                </Box>
              ) : undefined
            }
            data-testid='sales.send-to-customer-step.submit-button'
          >
            Yes, Submit
          </Button>
        </Box>
      </DialogActions>

      {isPreviewOpen && (
        <QuoteEmailPreviewDialog
          quoteGroupId={quoteGroupId}
          emailNote={emailNote}
          isSending={isSending}
          onBack={handlePreviewBack}
          onSend={handleSubmit}
        />
      )}

      {isNoSubscribersWarningOpen && (
        <NoSalesSubscribersModal
          isSendingToSelf={false}
          isDownloading={false}
          onCancel={handleNoSubscribersWarningClose}
          onSendToSelf={handleSendToSelf}
          onDownload={handleDownload}
        />
      )}
    </>
  )
}
